import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AskFrankDesk, {
    DeskRibbon,
    DeskTitle,
    DeskNote,
    DeskPanel,
    DeskField,
    DeskEmpty,
    DESK_INPUT_CLASS,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
    TINY_BUTTON_CLASS,
} from '../../../../components/staff/askfrank/AskFrankDesk';
import {
    getAskFrankPhrases,
    saveAskFrankPhrase,
    deleteAskFrankPhrase,
    getAskFrankLinks,
    saveAskFrankLink,
    deleteAskFrankLink,
} from '../../../../services/api';

const EMPTY_EXAMPLE = { slot: 'placeholder', questionType: 'any', text: '', weight: 1, enabled: true };
const EMPTY_LINK = { title: '', subtitle: '', url: '', thumbnailUrl: '', sortOrder: 0, enabled: true };

function AskFrankFrontPage({ userData }) {
    const [phrases, setPhrases] = useState([]);
    const [links, setLinks] = useState([]);
    const [exampleForm, setExampleForm] = useState(EMPTY_EXAMPLE);
    const [editingExample, setEditingExample] = useState(null);
    const [linkForm, setLinkForm] = useState(EMPTY_LINK);
    const [editingLink, setEditingLink] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        const [phraseRes, linkRes] = await Promise.all([getAskFrankPhrases(), getAskFrankLinks()]);

        if (phraseRes.success) setPhrases(phraseRes.phrases || []);
        if (linkRes.success) setLinks(linkRes.links || []);
    }, []);

    useEffect(() => { load(); }, [load]);

    if (userData && !userData.hasAdminRole) return <Navigate to="/staff" replace />;

    const withBusy = async (fn, message) => {
        setBusy(true);
        try {
            const res = await fn();
            if (res.success) {
                toast.success(res.message || message);
                await load();
            } else {
                toast.error(res.message || 'That did not work');
            }
            return res;
        } finally {
            setBusy(false);
        }
    };

    const examples = phrases.filter(phrase => phrase.slot === 'placeholder');
    const shelved = phrases.length - examples.length;

    const resetExample = () => {
        setExampleForm(EMPTY_EXAMPLE);
        setEditingExample(null);
    };

    const submitExample = async (event) => {
        event.preventDefault();

        if (!exampleForm.text.trim()) {
            toast.error('Type an example question');
            return;
        }

        const res = await withBusy(() => saveAskFrankPhrase(editingExample, exampleForm), 'Example saved');
        if (res.success) resetExample();
    };

    const resetLink = () => {
        setLinkForm(EMPTY_LINK);
        setEditingLink(null);
    };

    const submitLink = async (event) => {
        event.preventDefault();

        if (!linkForm.title.trim() || !linkForm.url.trim()) {
            toast.error('A title and URL are required');
            return;
        }

        const res = await withBusy(() => saveAskFrankLink(editingLink, linkForm), 'Link saved');
        if (res.success) resetLink();
    };

    return (
        <AskFrankDesk userData={userData}>
            <DeskRibbon>
                <DeskTitle>Front Page</DeskTitle>
                <DeskNote>What a visitor sees before they have asked Frank anything.</DeskNote>
            </DeskRibbon>

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                <DeskPanel
                    title="Example questions"
                    right={<span className="text-[11px] uppercase text-white/70">{examples.length} lines</span>}
                >
                    <p className="text-[12px] text-gray-500">
                        These rotate through the AskFrank search box as the greyed out suggestion. Write them the way a
                        visitor would type, and keep them to things Frank can actually answer.
                    </p>

                    <form onSubmit={submitExample} className="mt-3 flex flex-wrap items-end gap-2 border-b border-gray-300 pb-3">
                        <DeskField label="Example question" className="min-w-[220px] flex-1">
                            <input
                                type="text"
                                value={exampleForm.text}
                                onChange={(e) => setExampleForm({ ...exampleForm, text: e.target.value })}
                                placeholder="when were pumpkins released?"
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="Order" hint="higher first">
                            <input
                                type="number"
                                step="0.1"
                                min="0.1"
                                max="9.9"
                                value={exampleForm.weight}
                                onChange={(e) => setExampleForm({ ...exampleForm, weight: Number(e.target.value) })}
                                className="w-[80px] rounded border border-gray-300 px-2 py-2 text-sm"
                            />
                        </DeskField>

                        <button type="submit" disabled={busy} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                            {editingExample ? 'Save' : 'Add'}
                        </button>

                        {editingExample && (
                            <button type="button" onClick={resetExample} className={DESK_BUTTON_CLASS}>Cancel</button>
                        )}
                    </form>

                    {examples.length === 0 ? (
                        <DeskEmpty>No examples yet.</DeskEmpty>
                    ) : (
                        <div className="mt-3 max-h-[420px] overflow-y-auto border border-gray-200 bg-white">
                            {examples.map(phrase => (
                                <div key={phrase.id} className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-2 py-[6px] last:border-b-0">
                                    <span className="min-w-0 flex-1 truncate text-[13px] text-gray-800">{phrase.text}</span>
                                    <span className="shrink-0 text-[11px] text-gray-400">x{phrase.weight}</span>

                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => withBusy(
                                            () => saveAskFrankPhrase(phrase.id, { ...phrase, enabled: !phrase.enabled }),
                                            'Updated'
                                        )}
                                        className={`shrink-0 px-2 py-[2px] text-[10px] uppercase text-white disabled:opacity-50 ${
                                            phrase.enabled ? 'bg-green-600' : 'bg-gray-400'
                                        }`}
                                    >
                                        {phrase.enabled ? 'On' : 'Off'}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditingExample(phrase.id);
                                            setExampleForm({
                                                slot: 'placeholder',
                                                questionType: phrase.questionType || 'any',
                                                text: phrase.text,
                                                weight: phrase.weight,
                                                enabled: phrase.enabled,
                                            });
                                        }}
                                        className={TINY_BUTTON_CLASS}
                                    >
                                        Edit
                                    </button>

                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => withBusy(() => deleteAskFrankPhrase(phrase.id), 'Removed')}
                                        className="shrink-0 bg-red-700 px-2 py-[2px] text-[10px] uppercase text-white disabled:opacity-50"
                                    >
                                        Delete
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {shelved > 0 && (
                        <p className="mt-3 border-t border-gray-200 pt-2 text-[11px] text-gray-400">
                            There are {shelved} older lines on file from the days Frank read his answers out of a phrase
                            book. Nothing reads them now, so they are not shown here. They can be wired back up or
                            cleared out whenever you want.
                        </p>
                    )}
                </DeskPanel>

                <DeskPanel
                    title="Links on the home page"
                    right={<span className="text-[11px] uppercase text-white/70">{links.length} links</span>}
                >
                    <p className="text-[12px] text-gray-500">
                        One list. Every link shows on the AskFrank welcome page, and the first six fill the banner grid.
                    </p>

                    <form onSubmit={submitLink} className="mt-3 grid grid-cols-1 gap-3 border-b border-gray-300 pb-3 sm:grid-cols-2">
                        <DeskField label="Title" required>
                            <input
                                type="text"
                                value={linkForm.title}
                                onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="Order" hint="low numbers first">
                            <input
                                type="number"
                                value={linkForm.sortOrder}
                                onChange={(e) => setLinkForm({ ...linkForm, sortOrder: Number(e.target.value) })}
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="URL" required className="sm:col-span-2">
                            <input
                                type="text"
                                value={linkForm.url}
                                onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                                placeholder="https://example.com"
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="Subtitle">
                            <input
                                type="text"
                                value={linkForm.subtitle}
                                onChange={(e) => setLinkForm({ ...linkForm, subtitle: e.target.value })}
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="Thumbnail URL">
                            <input
                                type="text"
                                value={linkForm.thumbnailUrl}
                                onChange={(e) => setLinkForm({ ...linkForm, thumbnailUrl: e.target.value })}
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <label className="flex items-center gap-2 text-[12px] text-gray-700">
                            <input
                                type="checkbox"
                                checked={linkForm.enabled}
                                onChange={(e) => setLinkForm({ ...linkForm, enabled: e.target.checked })}
                            />
                            Visible
                        </label>

                        <div className="flex flex-wrap gap-2">
                            <button type="submit" disabled={busy} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                                {editingLink ? 'Save link' : 'Add link'}
                            </button>

                            {editingLink && (
                                <button type="button" onClick={resetLink} className={DESK_BUTTON_CLASS}>Cancel</button>
                            )}
                        </div>
                    </form>

                    {links.length === 0 ? (
                        <DeskEmpty>None yet.</DeskEmpty>
                    ) : (
                        <div className="mt-3 max-h-[420px] overflow-y-auto border border-gray-200 bg-white">
                            {links.map(link => (
                                <div key={link.id} className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-2 py-[6px] last:border-b-0">
                                    <span className="w-[28px] shrink-0 text-[11px] text-gray-400">#{link.sortOrder}</span>

                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-[13px] text-gray-800">
                                            {link.title}
                                            {!link.enabled && <span className="ml-2 text-[10px] uppercase text-red-700">hidden</span>}
                                        </p>
                                        <p className="truncate text-[11px] text-gray-400">{link.url}</p>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditingLink(link.id);
                                            setLinkForm({
                                                title: link.title,
                                                subtitle: link.subtitle || '',
                                                url: link.url,
                                                thumbnailUrl: link.thumbnailUrl || '',
                                                sortOrder: link.sortOrder,
                                                enabled: link.enabled,
                                            });
                                        }}
                                        className={TINY_BUTTON_CLASS}
                                    >
                                        Edit
                                    </button>

                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => withBusy(() => deleteAskFrankLink(link.id), 'Link removed')}
                                        className="shrink-0 bg-red-700 px-2 py-[2px] text-[10px] uppercase text-white disabled:opacity-50"
                                    >
                                        Delete
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </DeskPanel>
            </div>
        </AskFrankDesk>
    );
}

export default AskFrankFrontPage;
